/**
 * ForslagCard - Viser ett AI-foreslått oppfølgingsforslag
 *
 * Bruker kan:
 * - Justere dato
 * - Endre prioritet
 * - Endre type
 * - Godkjenne
 * - Avvise
 */

import React, { useState } from 'react';
import './ForslagCard.css';

const OPPFOLGING_TYPER = [
  { value: 'frist', label: '⚖️ Frist', beskrivelse: 'Lovpålagt eller avtalefestet frist' },
  { value: 'purring', label: '📮 Purring', beskrivelse: 'Ubesvart henvendelse' },
  { value: 'møte', label: '🤝 Møte', beskrivelse: 'Fysisk eller digitalt møte' },
  { value: 'telefonsamtale', label: '📞 Telefonsamtale', beskrivelse: 'Oppfølgingssamtale' },
  { value: 'dokumentasjon', label: '📄 Dokumentasjon', beskrivelse: 'Innhent/send dokumenter' },
  { value: 'intern_oppgave', label: '📋 Intern oppgave', beskrivelse: 'Intern utredning/vurdering' },
  { value: 'klient_kontakt', label: '👤 Klient-kontakt', beskrivelse: 'Kontakt klient' },
  { value: 'motpart_kontakt', label: '⚔️ Motpart-kontakt', beskrivelse: 'Kontakt motpart' }
];

const PRIORITETER = [
  { value: 'lav', label: 'Lav', color: '#10b981' },
  { value: 'middels', label: 'Middels', color: '#f59e0b' },
  { value: 'høy', label: 'Høy', color: '#ef4444' }
];

export default function ForslagCard({ forslag, onGodkjenn, onAvvis, onJuster }) {
  const [redigeringsmodus, setRedigeringsmodus] = useState(false);
  const [redigertDato, setRedigertDato] = useState(forslag.foreslatt_dato);
  const [redigertPrioritet, setRedigertPrioritet] = useState(forslag.prioritet);
  const [redigertType, setRedigertType] = useState(forslag.type);
  const [laster, setLaster] = useState(false);

  const typeInfo = OPPFOLGING_TYPER.find(t => t.value === (redigeringsmodus ? redigertType : forslag.type));
  const prioritetInfo = PRIORITETER.find(p => p.value === (redigeringsmodus ? redigertPrioritet : forslag.prioritet));

  const handleLagreEndringer = async () => {
    setLaster(true);
    try {
      await onJuster({
        foreslatt_dato: redigertDato,
        prioritet: redigertPrioritet,
        type: redigertType
      });
      setRedigeringsmodus(false);
    } catch (error) {
      console.error('Feil ved lagring:', error);
      alert('Kunne ikke lagre endringer');
    } finally {
      setLaster(false);
    }
  };

  const handleGodkjenn = async () => {
    setLaster(true);
    try {
      // Hvis redigert, send de nye verdiene
      if (redigeringsmodus) {
        await onGodkjenn({
          foreslatt_dato: redigertDato,
          prioritet: redigertPrioritet,
          type: redigertType
        });
      } else {
        await onGodkjenn();
      }
    } catch (error) {
      console.error('Feil ved godkjenning:', error);
      alert('Kunne ikke godkjenne forslag');
    } finally {
      setLaster(false);
    }
  };

  const handleAvvis = async () => {
    if (window.confirm('Er du sikker på at du vil avvise dette forslaget?')) {
      setLaster(true);
      try {
        await onAvvis();
      } catch (error) {
        console.error('Feil ved avvisning:', error);
        alert('Kunne ikke avvise forslag');
      } finally {
        setLaster(false);
      }
    }
  };

  const formaterDato = (datoString) => {
    const dato = new Date(datoString);
    return dato.toLocaleDateString('nb-NO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const dagerTil = (datoString) => {
    const dato = new Date(datoString);
    const iDag = new Date();
    iDag.setHours(0, 0, 0, 0);
    dato.setHours(0, 0, 0, 0);

    const diffMs = dato - iDag;
    const diffDager = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDager === 0) return 'I dag';
    if (diffDager === 1) return 'I morgen';
    if (diffDager < 0) return `${Math.abs(diffDager)} dager siden`;
    return `Om ${diffDager} dager`;
  };

  return (
    <div className={`forslag-card forslag-prioritet-${redigeringsmodus ? redigertPrioritet : forslag.prioritet}`}>
      <div className="forslag-header">
        <div className="forslag-type-badge">
          {typeInfo?.label || forslag.type}
        </div>
        <div className="forslag-prioritet-badge" style={{ backgroundColor: prioritetInfo?.color }}>
          {prioritetInfo?.label || forslag.prioritet}
        </div>
      </div>

      <div className="forslag-body">
        <h4 className="forslag-beskrivelse">
          {forslag.beskrivelse}
        </h4>

        {!redigeringsmodus ? (
          <>
            <div className="forslag-dato">
              <span className="forslag-dato-ikon">📅</span>
              <span className="forslag-dato-tekst">
                {formaterDato(forslag.foreslatt_dato)}
              </span>
              <span className="forslag-dato-relativ">
                ({dagerTil(forslag.foreslatt_dato)})
              </span>
            </div>

            {forslag.begrunnelse && (
              <div className="forslag-begrunnelse">
                <strong>AI-begrunnelse:</strong>
                <p>{forslag.begrunnelse}</p>
              </div>
            )}
          </>
        ) : (
          <div className="forslag-rediger">
            <div className="forslag-rediger-felt">
              <label>Dato:</label>
              <input
                type="date"
                value={redigertDato}
                onChange={(e) => setRedigertDato(e.target.value)}
                className="forslag-input"
              />
              <small className="forslag-hint">{dagerTil(redigertDato)}</small>
            </div>

            <div className="forslag-rediger-felt">
              <label>Type:</label>
              <select
                value={redigertType}
                onChange={(e) => setRedigertType(e.target.value)}
                className="forslag-select"
              >
                {OPPFOLGING_TYPER.map(t => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="forslag-rediger-felt">
              <label>Prioritet:</label>
              <select
                value={redigertPrioritet}
                onChange={(e) => setRedigertPrioritet(e.target.value)}
                className="forslag-select"
              >
                {PRIORITETER.map(p => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="forslag-actions">
        {!redigeringsmodus ? (
          <>
            <button
              className="btn-forslag-godkjenn"
              onClick={handleGodkjenn}
              disabled={laster}
            >
              {laster ? '⏳' : '✅'} Godkjenn
            </button>
            <button
              className="btn-forslag-rediger"
              onClick={() => setRedigeringsmodus(true)}
              disabled={laster}
            >
              ✏️ Juster
            </button>
            <button
              className="btn-forslag-avvis"
              onClick={handleAvvis}
              disabled={laster}
            >
              ❌ Avvis
            </button>
          </>
        ) : (
          <>
            <button
              className="btn-forslag-lagre"
              onClick={handleLagreEndringer}
              disabled={laster}
            >
              💾 Lagre endringer
            </button>
            <button
              className="btn-forslag-godkjenn"
              onClick={handleGodkjenn}
              disabled={laster}
            >
              ✅ Godkjenn nå
            </button>
            <button
              className="btn-forslag-avbryt"
              onClick={() => {
                setRedigertDato(forslag.foreslatt_dato);
                setRedigertPrioritet(forslag.prioritet);
                setRedigertType(forslag.type);
                setRedigeringsmodus(false);
              }}
              disabled={laster}
            >
              Avbryt
            </button>
          </>
        )}
      </div>

      {forslag.ai_generated && (
        <div className="forslag-ai-badge">
          🤖 AI-generert
        </div>
      )}
    </div>
  );
}
